import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { getToken, isSupported, onMessage } from "firebase/messaging";
import { useEffect, useState } from "react";
import { messaging } from "~/libs/firebase";
import { addNewNotification } from "~/queries/use-subscribe-to-venue-notifications";
import { useUpdateCurrentVenue } from "~/queries/use-update-current-venue";
import { useVenueId } from "~/store";
import {
	shouldNotifyForDigitalMenuLocation,
	useDigitalMenuNotificationLocationIds,
} from "~/utils/digital-menu-notification-locations";
import { PresenceSnackbar } from "~served/ui-comps/components/overlay-presence/presence-overlays";

export const PushNotification = () => {
	const venueId = useVenueId();
	const [loading, setLoading] = useState(false);
	const [showRequest, setShowRequest] = useState(false);
	const [initMessaging, setInitMessaging] = useState(false);
	const updateVenue = useUpdateCurrentVenue();
	const [digitalMenuNotificationLocationIds] =
		useDigitalMenuNotificationLocationIds();

	useEffect(() => {
		async function start() {
			const isBrowserSupported = await isSupported().catch(() => false);
			if (!isBrowserSupported) return;

			setShowRequest(Notification.permission === "default");
			if (Notification.permission === "granted") {
				const token = await getToken(messaging, {
					vapidKey: process.env.FB_VAPID_KEY,
				});
				await updateVenue
					.mutateAsync({ input: { token } })
					.catch(console.error);
				setInitMessaging(!!token);
			}
		}

		if (venueId) void start();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [venueId]);

	useEffect(() => {
		if (initMessaging) {
			onMessage(messaging, (payload) => {
				const { data = {} } = payload;
				if (data.type === "assistance_request") {
					const shouldNotify = shouldNotifyForDigitalMenuLocation(
						data.locationId,
						digitalMenuNotificationLocationIds,
					);
					if (!shouldNotify) return;
					addNewNotification({
						id: data.id,
						message: data.title,
						type: "STAFF_ASSISTANCE",
						severity: "info",
					});
				}
				if (data.type === "payway_order_failure") {
					addNewNotification({
						id: data.id,
						message: data.title,
						type: "PAYWAY_ORDER_FAILURE",
						severity: "error",
					});
				}
				if (data.type === "order_placed") {
					const isDigitalMenuOrder = data.source === "qr";
					const shouldNotify =
						!isDigitalMenuOrder ||
						shouldNotifyForDigitalMenuLocation(
							data.locationId,
							digitalMenuNotificationLocationIds,
						);
					if (!shouldNotify) return;
					addNewNotification({
						id: data.id,
						message: data.title,
						type: "NEW_ORDER",
						severity: "warning",
						customProps: {
							status: data.status,
							locationId: data.locationId,
							source: data.source,
						},
					});
				}
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [digitalMenuNotificationLocationIds, initMessaging]);

	const handleRequestPermission = async () => {
		try {
			setLoading(true);
			const token = await getToken(messaging, {
				vapidKey: process.env.FB_VAPID_KEY,
			});
			await updateVenue.mutateAsync({ input: { token } });

			setInitMessaging(!!token);
			setLoading(false);
			setShowRequest(false);
		} catch (e) {
			console.error(e);
			setLoading(false);
			setShowRequest(false);
		}
	};

	if (showRequest)
		return (
			<PresenceSnackbar
				open
				autoHideDuration={3000}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Paper
					variant="elevation"
					sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
					<Typography variant="body2">
						Get notified about your customers activity when app is not being
						used
					</Typography>
					<Box
						display="flex"
						justifyContent="flex-end"
						gap={1}>
						<Button
							variant="outlined"
							color="inherit"
							disabled={loading}
							onClick={() => setShowRequest(false)}>
							Dismiss
						</Button>
						<LoadingButton
							loading={loading}
							onClick={handleRequestPermission}>
							Allow notification
						</LoadingButton>
					</Box>
				</Paper>
			</PresenceSnackbar>
		);

	return null;
};
